import { useCallback, useEffect, useMemo, useRef } from 'react'
import Modal from 'LuxKit/Modal/Modal'
import MaldivesDirectSearchFlightCalendar from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import type { DateRange } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/types'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useOfferCharterCalendarData'
import type { FlightsClassTypes } from 'constants/flight'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { parseAsBoolean, useQueryState } from 'nuqs'
import CharterBundleCalendarActions from './CharterBundleCalendarActions'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getCharterFareClassPolicy } from 'lib/charterFlights/charterOfferFareClassRestrictions'
import { useIntl } from 'react-intl'

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  onFareClassChange?: (fareClass: FlightsClassTypes) => void
  offer: App.HotelOffer
  duration: number
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  dateRangesByMonth: Record<string, Array<DateRange>>
  charterBundleRoute?: App.CharterBundleRoute
  isOpen: boolean
  onClose: () => void
  /** Callback when dates are selected */
  onDateSelect: (option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) => void
}

function CharterBundleCalendarModal(props: Props) {
  const intl = useIntl()
  const {
    offer,
    duration,
    rooms,
    checkInDate,
    selectedMonth,
    onMonthChange,
    fareClass,
    priceMode,
    onDurationChange,
    durationOptions,
    onFareClassChange,
    dateRangesByMonth,
    charterBundleRoute,
    isOpen,
    onClose,
    onDateSelect,
  } = props

  // Fetch charter calendar data inside the modal so it responds to updates
  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms,
    fareClass,
    dateRangesByMonth,
    charterBundleRoute,
  })

  const modalBodyRef = useRef<HTMLDivElement | null>(null)
  const isDesktop = useScreenSizeUp('desktop')

  // Read luxPlus state from URL (same as parent component)
  // Default to false if brand is not Luxury Escapes
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(config.BRAND === LUXURY_ESCAPES),
  )

  // Filter durationOptions to only include those with matching packages
  const filteredDurationOptions = useMemo(() => {
    return durationOptions.filter(option => {
      // Check if offer has a package with matching duration
      return offer.packages.some(pkg => pkg.duration === option.value)
    })
  }, [durationOptions, offer.packages])
  const charterFareClassPolicy = useMemo(() => {
    return getCharterFareClassPolicy({
      campaignTags: offer.campaignTags,
      occupancies: rooms,
    })
  }, [offer.campaignTags, rooms])

  // If current duration doesn't have a matching package, switch to first available duration
  useEffect(() => {
    const hasMatchingPackage = offer.packages.some(pkg => pkg.duration === duration)
    if (!hasMatchingPackage && filteredDurationOptions.length > 0) {
      const firstAvailableDuration = filteredDurationOptions[0].value
      if (firstAvailableDuration !== duration) {
        onDurationChange(firstAvailableDuration)
      }
    }
  }, [duration, offer.packages, filteredDurationOptions, onDurationChange])

  // Calculate selected option ID based on current checkIn date and duration
  const selectedOptionId = useMemo(() => {
    if (!checkInDate) return undefined
    return `${checkInDate}-${duration}n`
  }, [checkInDate, duration])

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) => {
    // Return the selected dates via callback
    onDateSelect(option)
  }, [onDateSelect])

  const calendarProps = {
    onDateSelect: handleDateSelect,
    nights: duration,
    occupancies: rooms,
    luxPlusEnabled,
    itemBundleDataByDate: flightCalendarData.itemBundleDataByDate,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    fareClass,
    priceMode,
    selectedOptionId,
    mode: 'offer' as const,
    offer,
  }

  const actionsElement = (
    <CharterBundleCalendarActions
      nights={duration}
      onDurationChange={onDurationChange}
      durationOptions={filteredDurationOptions}
      luxPlusEnabled={luxPlusEnabled}
      onLuxPlusToggle={setLuxPlusEnabled}
      fareClass={fareClass}
      onFareClassChange={onFareClassChange}
      availableFareClasses={charterFareClassPolicy.allowedFareClasses}
    />
  )

  const selectTripDatesLabel = intl.formatMessage({
    id: '8y9vgMTYZTXakoijFb6D',
    defaultMessage: 'Select your trip dates',
    description: 'Title for the charter bundle calendar modal in the trip planner',
  })

  const desktopTitle = (
    <Group direction="horizontal" horizontalAlign="space-between" gap={16} verticalAlign="center" fullWidth>
      <span>{selectTripDatesLabel}</span>
      {actionsElement}
    </Group>
  )

  const mobileTitle = selectTripDatesLabel

  const title = isDesktop ? desktopTitle : mobileTitle

  return (
    <Modal
      isOpen={isOpen}
      ref={modalBodyRef}
      title={title}
      size="XL"
      isPrimaryActionHidden
      onClose={onClose}
    >
      {isDesktop && <MaldivesDirectSearchFlightCalendar {...calendarProps} />}
      {!isDesktop && <MaldivesDirectSearchFlightCalendarMobile {...calendarProps} headerActions={actionsElement} />}
    </Modal>
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer! as App.HotelOffer,
  duration: state.duration,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
  charterBundleRoute: state.charterBundleRoute,
}), CharterBundleCalendarModal)
